// Pashmak (پشمک), the fluffy Persian cat tutor in a turquoise scarf: an SVG port
// of ios/FarsiTutor/Features/Character/PashmakView.swift (+ OutfitArt.swift and
// Android character/Pashmak.kt), with every part animated in CSS.
//
// Performance: the art is a memoized subtree that only re-renders when the mood,
// outfit or `plain` change. The live audio `level` reaches the mouth, the head bob
// and the sound rings through one CSS variable (--lvl) on the <svg>, so lip-sync
// at 60 fps never re-renders the drawing. Animations pause while the cat is off
// screen and stop entirely under prefers-reduced-motion.

import { memo, useEffect, useId, useRef, type CSSProperties } from "react";
import { useLearn, type Outfit } from "../lib/state";
import { C, arc, catSmile, drop, ear, happyEye, khatam, roundedTriangle } from "./pashmak/geometry";
import { GlassesArt, Halo, HatArt, HeldArt, ScarfArt, heldOffset } from "./pashmak/outfit";
import "./pashmak/pashmak.css";

export type Mood = "idle" | "sleeping" | "listening" | "thinking" | "talking" | "happy" | "confused";

export interface PashmakProps {
  mood?: Mood;
  size?: number;
  /** 0…1 live audio level (mic while listening, voice while talking). */
  level?: number;
  /** Defaults to what the learner is wearing. */
  outfit?: Outfit;
  /** Hide the outfit (e.g. tiny avatars). */
  plain?: boolean;
  className?: string;
}

/** What each mood says to screen readers. */
export const MOOD_LABEL: Record<Mood, string> = {
  idle: "waiting for you",
  sleeping: "waking up",
  listening: "listening",
  thinking: "thinking",
  talking: "speaking",
  happy: "happy",
  confused: "confused",
};

export const MOODS: Mood[] = ["idle", "sleeping", "listening", "thinking", "talking", "happy", "confused"];

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

export function Pashmak({ mood = "idle", size = 120, level = 0, outfit, plain = false, className }: PashmakProps) {
  const worn = useLearn((s) => s.outfit);
  const look = outfit ?? worn;
  const u = "pm" + useId().replace(/[^a-zA-Z0-9]/g, "");
  const ref = useRef<SVGSVGElement>(null);

  // Pause the animations while off screen (bazaar grids can hold dozens of cats).
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => el.classList.toggle("pm-off", !e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const box = plain ? 216 : 260;
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox={`0 0 ${box} ${box}`}
      className={`pm pm-${mood}${plain ? " pm-plain" : ""}${className ? ` ${className}` : ""}`}
      style={{ "--lvl": clamp01(level) } as CSSProperties}
      role="img"
      aria-label={`Pashmak the cat, ${MOOD_LABEL[mood]}`}
      data-mood={mood}
      data-outfit={plain ? "" : [look.hat, look.face, look.neck, look.held, look.halo].filter(Boolean).join(" ")}
    >
      <PashmakArt
        mood={mood}
        plain={plain}
        u={u}
        hat={look.hat}
        face={look.face}
        neck={look.neck}
        held={look.held}
        halo={look.halo}
        origin={plain ? "108 128" : "130 130"}
      />
    </svg>
  );
}

// ---------------------------------------------------------------- the drawing

interface ArtProps {
  mood: Mood;
  plain: boolean;
  u: string;
  hat: string | null;
  face: string | null;
  neck: string;
  held: string | null;
  halo: string;
  origin: string;
}

const EARS: Partial<Record<Mood, [number, number]>> = {
  listening: [6, -10],
  sleeping: [38, 8],
  confused: [28, 2],
  happy: [12, -6],
};
const TILT: Partial<Record<Mood, number>> = { listening: -7, thinking: 5, confused: 9 };
const LOOK: Partial<Record<Mood, [number, number]>> = { thinking: [4, -5], listening: [-2, 0] };

/** Exported for the PNG export (renders the same drawing into a standalone SVG). */
export const PashmakArt = memo(function PashmakArt({ mood, plain, u, hat, face, neck, held, halo, origin }: ArtProps) {
  const [earAngle, earLift] = EARS[mood] ?? [18, 0];
  const tilt = TILT[mood] ?? 0;
  const tailClass = mood === "happy" ? "pm-tail pm-tail-happy" : mood === "listening" ? "pm-tail pm-tail-slow" : "pm-tail";
  const wobble = mood === "talking" ? "pm-wobble-talk" : mood === "thinking" ? "pm-wobble-think" : "pm-wobble";
  const neckId = plain ? "neck.firuze" : neck;
  const heldPos = held ? heldOffset(held) : null;

  return (
    <g transform={`translate(${origin})`}>
      <defs>
        <linearGradient id={`${u}-tail`} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor={C.fur} />
          <stop offset="1" stopColor={C.ginger} stopOpacity={0.8} />
        </linearGradient>
        <radialGradient id={`${u}-iris`} cx=".5" cy="1" r=".62">
          <stop offset="0" stopColor="#C8782A" />
          <stop offset="1" stopColor="#C8782A" stopOpacity={0} />
        </radialGradient>
      </defs>

      <g className={mood === "happy" ? "pm-bounce" : undefined}>
        {!plain && <Halo id={halo} u={u} />}

        {/* Tail, pivoting at its base behind the body (cropped out of the plain avatar) */}
        {!plain && (
          <g transform="translate(62 121) rotate(35)">
            <g className={tailClass}>
              <rect x={-15} y={-86} width={30} height={86} rx={15} fill={`url(#${u}-tail)`} stroke={C.furShade} strokeWidth={2} />
            </g>
          </g>
        )}

        {/* Body + paws + scarf, breathing from the bottom */}
        <g transform="translate(0 130)">
          <g className="pm-breathe">
            <g transform="translate(0 -130)">
              <ellipse cy={88} rx={64} ry={46} fill={C.fur} stroke={C.outline} strokeWidth={2.5} />
              <ellipse cy={100} rx={35} ry={20} fill={C.furShade} />
              <Paw x={-30} />
              <g className={mood === "happy" ? "pm-wave" : undefined}>
                <Paw x={30} />
              </g>
              <g transform="translate(0 70)">
                <ScarfArt id={neckId} u={u} />
              </g>
            </g>
          </g>
        </g>

        {/* Head: ears, fluff, face, glasses and hat tilt together */}
        <g transform="translate(0 -18)">
          <g style={mood === "talking" ? { transform: "translateY(calc(var(--lvl, 0) * -4px))" } : undefined} className="pm-headbob">
            <g transform="translate(0 52)">
              <g className="pm-tilt" style={{ transform: `rotate(${tilt}deg)` }}>
                <g className={wobble}>
                  <g transform="translate(0 -52)">
                    <Ear side={-1} angle={earAngle} lift={earLift} />
                    <Ear side={1} angle={earAngle} lift={earLift} />
                    <FluffyHead />
                    <Face mood={mood} u={u} />
                    {!plain && <GlassesArt id={face} u={u} />}
                    {!plain && <HatArt id={hat} u={u} />}
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>

        {!plain && held && heldPos && (
          <g transform={`translate(${heldPos.x} ${heldPos.y})`}>
            <HeldArt id={held} u={u} />
          </g>
        )}

        <Overlays mood={mood} />
      </g>
    </g>
  );
});

function Paw({ x }: { x: number }) {
  return (
    <g transform={`translate(${x} 128)`}>
      <ellipse rx={17} ry={11} fill={C.fur} stroke={C.furShade} strokeWidth={1.5} />
      {[-7, 0, 7].map((dx) => (
        <circle key={dx} cx={dx} cy={3} r={2} fill={C.earPink} opacity={0.7} />
      ))}
    </g>
  );
}

const EAR_PATH = ear();
const EAR_INNER = ear(42 * 0.55, 46 * 0.55);

function Ear({ side, angle, lift }: { side: -1 | 1; angle: number; lift: number }) {
  return (
    <g transform={`translate(${side * 46} -51)`}>
      <g
        className="pm-ear"
        style={{ transform: `translateY(${lift}px) rotate(${side * angle}deg)${side > 0 ? " scaleX(-1)" : ""}` }}
      >
        <path d={EAR_PATH} fill={C.fur} stroke={C.outline} strokeWidth={2} strokeLinejoin="round" />
        <path d={EAR_INNER} fill={C.earPink} />
      </g>
    </g>
  );
}

const TUFTS = Array.from({ length: 16 }, (_, i) => {
  const a = (i / 16) * 2 * Math.PI;
  return [Math.cos(a) * 68, Math.sin(a) * 62] as const;
});

/** The fluffy head silhouette: 16 tufts + the face. Drawn twice, outlined then filled, so only the outer edge gets a line. */
function FluffyHead() {
  const shape = (outline: boolean) => (
    <g fill={C.fur} stroke={outline ? C.outline : "none"} strokeWidth={outline ? 5 : 0}>
      {TUFTS.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={17} />
      ))}
      <ellipse rx={78} ry={71} />
    </g>
  );
  return (
    <g>
      {shape(true)}
      {shape(false)}
      <ellipse cy={-52} rx={27} ry={15} fill={C.ginger} opacity={0.45} />
      <ellipse cx={44} cy={-34} rx={15} ry={9} fill={C.ginger} opacity={0.3} />
    </g>
  );
}

const HAPPY_EYE = happyEye(24, 12);
const SLEEP_EYE = happyEye(22, 8); // rotated 180° below
const SMILE = catSmile(26, 9);
const NOSE = roundedTriangle(13, 9);

function Face({ mood, u }: { mood: Mood; u: string }) {
  const blush = mood === "happy" ? 0.35 : 0.2;
  const open = mood === "talking" ? "min(1, calc(0.15 + var(--lvl, 0) * 1.4))" : mood === "happy" ? "0.55" : mood === "confused" ? "0.2" : null;
  return (
    <g>
      <ellipse cx={-46} cy={20} rx={12} ry={6} fill={C.pomegranate} opacity={blush} />
      <ellipse cx={46} cy={20} rx={12} ry={6} fill={C.pomegranate} opacity={blush} />
      <Eye x={-30} mood={mood} u={u} />
      <Eye x={30} mood={mood} u={u} />
      <path d={NOSE} transform="translate(0 16)" fill={C.earPink} />
      <g transform="translate(0 27)">
        {open === null ? (
          <path d={SMILE} fill="none" stroke={C.eyeDark} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <g
            className="pm-mouth"
            style={{
              ["--open" as string]: open,
              transform: "translateY(calc(var(--open) * 5px)) scale(calc((16 + 6 * var(--open)) / 22), calc((3 + 17 * var(--open)) / 20))",
            }}
          >
            <ellipse rx={11} ry={10} fill="#7A2233" />
            <ellipse cy={5.5} rx={6} ry={3.5} fill={C.earPink} />
          </g>
        )}
      </g>
      <Whiskers side={-1} />
      <Whiskers side={1} />
    </g>
  );
}

function Eye({ x, mood, u }: { x: number; mood: Mood; u: string }) {
  if (mood === "happy")
    return <path d={HAPPY_EYE} transform={`translate(${x} -4)`} fill="none" stroke={C.eyeDark} strokeWidth={4} strokeLinecap="round" />;
  if (mood === "sleeping")
    return <path d={SLEEP_EYE} transform={`translate(${x} -4) rotate(180)`} fill="none" stroke={C.eyeDark} strokeWidth={3.5} strokeLinecap="round" />;
  const big = mood === "listening" ? 1.12 : mood === "confused" ? 0.9 : 1;
  const [lx, ly] = LOOK[mood] ?? [0, 0];
  const drift = !LOOK[mood];
  return (
    <g transform={`translate(${x} -4)`}>
      <g className="pm-blink">
        <g className="pm-blink2">
          <g className={drift ? "pm-look" : undefined}>
            <g transform={`translate(${lx} ${ly}) scale(${big})`}>
              <ellipse rx={13} ry={16} fill={C.eyeDark} />
              <ellipse rx={13} ry={16} fill={`url(#${u}-iris)`} opacity={0.7} />
              <circle cx={4 + lx * 0.5} cy={-6 + ly * 0.5} r={5} fill="#fff" />
              <circle cx={-5 + lx * 0.3} cy={5 + ly * 0.3} r={2} fill="#fff" opacity={0.8} />
            </g>
          </g>
        </g>
      </g>
    </g>
  );
}

function Whiskers({ side }: { side: -1 | 1 }) {
  return (
    <g transform={`translate(${side * 36} 22) scale(${side} 1)`}>
      <g className="pm-whisk">
        {[-10, 0, 10].map((a) => (
          <rect key={a} x={0} y={-0.8} width={34} height={1.6} rx={0.8} fill={C.eyeDark} opacity={0.45} transform={`rotate(${a})`} />
        ))}
      </g>
    </g>
  );
}

// ---------------------------------------------------------------- mood overlays (decorative)

const RING = arc(12, 0.6, 0.9);
const SPARKLES: [number, number, number][] = [
  [-100, -90, 0],
  [100, -70, 0.3],
  [-90, 40, 0.6],
  [104, 30, 0.15],
  [0, -130, 0.45],
];
const STAR20 = khatam(0, 0, 20);
const DROP = drop(12, 18);

function Overlays({ mood }: { mood: Mood }) {
  switch (mood) {
    case "thinking":
      return (
        <g transform="translate(86 -108)" aria-hidden="true">
          <circle cx={-30} cy={34} r={5} fill="#fff" />
          <circle cx={-18} cy={20} r={8} fill="#fff" />
          <rect x={-35} y={-20} width={70} height={40} rx={20} fill="#fff" className="pm-bubble" />
          {[0, 1, 2].map((i) => (
            <g key={i} transform={`translate(${(i - 1) * 16} 0)`}>
              <circle className="pm-dot" style={{ animationDelay: `${i * 0.13}s` }} r={4.5} fill={i === 1 ? C.firuze : C.saffron} />
            </g>
          ))}
        </g>
      );
    case "listening":
      return (
        <g transform="translate(-104 -78) rotate(-20)" aria-hidden="true">
          <g className="pm-rings">
            {[0, 1, 2].map((i) => (
              <path
                key={i}
                className="pm-ring"
                style={{ animationDelay: `${-(i / 3) * 0.625}s` }}
                d={RING}
                fill="none"
                stroke={C.saffron}
                strokeWidth={3}
                strokeLinecap="round"
              />
            ))}
          </g>
        </g>
      );
    case "sleeping":
      return (
        <g transform="translate(66 -84)" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <text
              key={i}
              className="pm-ze"
              style={{ animationDelay: `${-(i / 3) * 2}s`, fontSize: 22 + i * 6 }}
              textAnchor="middle"
              dominantBaseline="central"
              fontWeight={800}
              fill="var(--lapis, #1F3C88)"
            >
              ز
            </text>
          ))}
        </g>
      );
    case "happy":
      return (
        <g aria-hidden="true">
          {SPARKLES.map(([x, y, delay], i) => (
            <g key={i} transform={`translate(${x} ${y})`}>
              <g className="pm-spin-6">
                <path className="pm-sparkle" style={{ animationDelay: `${-delay * 2}s` }} d={STAR20} fill={i % 2 === 0 ? C.saffron : C.firuzeLight} />
              </g>
            </g>
          ))}
        </g>
      );
    case "confused":
      return (
        <g aria-hidden="true">
          <g transform="translate(86 -104)">
            <g className="pm-qbob">
              <text className="pm-q" textAnchor="middle" dominantBaseline="central" fontSize={44} fontWeight={900} fill={C.saffron}>
                ؟
              </text>
            </g>
          </g>
          <g transform="translate(62 -60)">
            <path className="pm-drop" d={DROP} fill={C.firuzeLight} />
          </g>
        </g>
      );
    default:
      return null;
  }
}
